/**
 * Step cliente del funnel onboarding.
 * Si può tornare agli step già fatti; non si può saltare avanti.
 */
import type { OnboardingPublicPayload } from "@/lib/onboarding-public";
import {
  onboardingPublicPath,
  resolveOnboardingPaymentMethod,
} from "@/types/onboarding";

export type CustomerStepId = "terms" | "payment" | "call" | "completed";

export type CustomerStepState = "done" | "current" | "todo" | "locked";

export interface CustomerStepDef {
  id: CustomerStepId;
  label: string;
  shortLabel: string;
}

export const CUSTOMER_STEPS: CustomerStepDef[] = [
  { id: "terms", label: "Termini", shortLabel: "1" },
  { id: "payment", label: "Pagamento", shortLabel: "2" },
  { id: "call", label: "Call", shortLabel: "3" },
  { id: "completed", label: "Fine", shortLabel: "4" },
];

export function customerStepPath(token: string, id: CustomerStepId): string {
  if (id === "terms") return onboardingPublicPath(token, "terms");
  if (id === "payment") return onboardingPublicPath(token, "payment");
  if (id === "call") return onboardingPublicPath(token, "call");
  return onboardingPublicPath(token, "completed");
}

/** True se lo step è già stato completato (si può riaprire). */
export function isCustomerStepDone(
  id: CustomerStepId,
  payload?: OnboardingPublicPayload | null,
): boolean {
  const ob = payload?.onboarding;
  if (!ob) return false;
  if (id === "terms") return !!ob.terms_accepted_at;
  if (id === "payment") {
    const method = resolveOnboardingPaymentMethod(ob.payment_method);
    if (method === "bonifico") {
      return !!ob.setup_fee_received_at || !!ob.access_ready;
    }
    if (method === "carta") {
      return !!ob.payment_succeeded_at;
    }
    // entrambi
    if (!ob.payment_succeeded_at) return false;
    const fee = Number(ob.setup_fee_amount ?? 0) || 0;
    if (fee > 0) return !!ob.setup_fee_received_at || !!ob.access_ready;
    return true;
  }
  if (id === "call") {
    return ob.call_status === "booked" || ob.call_status === "skipped";
  }
  return !!ob.access_ready && (ob.call_status === "booked" || ob.call_status === "skipped");
}

/** True se si può aprire lo step (fatto, corrente, o primo non ancora fatto in sequenza). */
export function isCustomerStepReachable(
  id: CustomerStepId,
  payload?: OnboardingPublicPayload | null,
): boolean {
  if (isCustomerStepDone(id, payload)) return true;

  // Primo step incompleto nella sequenza = raggiungibile
  for (const s of CUSTOMER_STEPS) {
    if (!isCustomerStepDone(s.id, payload)) {
      return s.id === id;
    }
  }
  return id === "completed";
}

/**
 * Navigazione pill: solo step già fatti o lo step corrente raggiungibile.
 * Non permette di saltare a step futuri locked.
 */
export function canOpenCustomerStep(
  target: CustomerStepId,
  current: CustomerStepId,
  payload?: OnboardingPublicPayload | null,
): boolean {
  if (target === current) return true;
  if (isCustomerStepDone(target, payload)) return true;
  // Solo il prossimo immediato se lo step corrente è già fatto
  const next = nextCustomerStep(current);
  if (next === target && isCustomerStepDone(current, payload)) return true;
  return false;
}

export function getCustomerStepStates(
  current: CustomerStepId,
  payload?: OnboardingPublicPayload | null,
): Record<CustomerStepId, CustomerStepState> {
  const result = {} as Record<CustomerStepId, CustomerStepState>;
  for (const step of CUSTOMER_STEPS) {
    if (step.id === current) {
      result[step.id] = "current";
    } else if (isCustomerStepDone(step.id, payload)) {
      result[step.id] = "done";
    } else if (isCustomerStepReachable(step.id, payload)) {
      result[step.id] = "todo";
    } else {
      result[step.id] = "locked";
    }
  }
  return result;
}

export function prevCustomerStep(id: CustomerStepId): CustomerStepId | null {
  const idx = CUSTOMER_STEPS.findIndex((s) => s.id === id);
  if (idx <= 0) return null;
  return CUSTOMER_STEPS[idx - 1]!.id;
}

export function nextCustomerStep(id: CustomerStepId): CustomerStepId | null {
  const idx = CUSTOMER_STEPS.findIndex((s) => s.id === id);
  if (idx < 0 || idx >= CUSTOMER_STEPS.length - 1) return null;
  return CUSTOMER_STEPS[idx + 1]!.id;
}

/** Primo step ancora da fare (per redirect se URL saltato). */
export function firstIncompleteCustomerStep(
  payload?: OnboardingPublicPayload | null,
): CustomerStepId {
  for (const s of CUSTOMER_STEPS) {
    if (!isCustomerStepDone(s.id, payload)) return s.id;
  }
  return "completed";
}

/** Hint breve sotto la barra sticky */
export function customerStepHint(
  id: CustomerStepId,
  payload?: OnboardingPublicPayload | null,
): string {
  const ob = payload?.onboarding;
  if (!ob) return "";
  if (id === "terms") {
    return ob.terms_accepted_at ? "Contratto accettato" : "Leggi e conferma";
  }
  if (id === "payment") {
    if (ob.access_ready) return "Pagamenti completati";
    const method = resolveOnboardingPaymentMethod(ob.payment_method);
    if (method === "bonifico") {
      return ob.setup_fee_received_at
        ? "Bonifico confermato"
        : "In attesa bonifico";
    }
    if (ob.payment_succeeded_at && method === "entrambi" && Number(ob.setup_fee_amount) > 0) {
      return "In attesa bonifico";
    }
    if (ob.payment_succeeded_at) return "Canone confermato";
    return method === "bonifico" ? "Bonifico totale" : "Abbonamento con carta";
  }
  if (id === "call") {
    if (ob.call_status === "booked") return "Call prenotata";
    if (ob.call_status === "skipped") return "Call in pending";
    return "Prenota o salta";
  }
  if (ob.access_ready) {
    return ob.unlock_mode === "existing_trial"
      ? "Accesso illimitato"
      : "Attivazione completa";
  }
  if (ob.tenant_is_trial) return "Prova attiva — bonifico in corso";
  return "In attesa sblocco";
}
